import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Check, ShieldAlert, FileCheck } from 'lucide-react';
import SeoMeta from '../components/SeoMeta';
import { useLocalizedUrl } from '../utils/urlHelper';
import { PageHero, SectionIntro, FaqList, faqSchema, ClosingCta, usePrimaryCta } from '../components/common/B2B';

// Documento de transporte ADR (nov-2026), contenido diseñado desde cero.
//
// OJO: en el ADR digital BlueCMR es pionera y no queremos que nos copien (Jesús,
// 4-nov). Por eso, a diferencia de las demás páginas de documento, el hero lleva
// una FOTO y no el documento real, no hay documento anotado y la página cuenta
// QUÉ resuelve y el marco legal —que es público—, no CÓMO lo hace la plataforma
// (el detalle vive en la documentación para clientes y en el portal de
// desarrolladores). Lo que se dice de la plataforma no pasa de lo que ya
// publicaba esta página.
//
// Jesús (4-nov): «dos obligaciones, un solo documento» (ADR + DeCA); la carta de
// porte puede no ser necesaria, así que no se presenta como tercera obligación.
//
// Terminología: el ADR 2025 lo llama «documento de transporte»; «carta de porte»
// queda reservada a CMR, CIM y aéreo. Los residuos quedan fuera (llevan su propio
// documento de identificación). Afirmaciones legales: las de la página anterior.
//
// Versión inglesa añadida el 4-nov-2026: traducción fiel, con el mismo nivel de
// detalle que la española (nada más sobre CÓMO lo hace la plataforma).

const PAGE_URL = { es: 'https://www.bluecmr.com/documentos/transporte-adr', en: 'https://www.bluecmr.com/en/documents/adr-transport-document' };
// Generada con GPT Image 2 (marketing/prompts-imagenes/hero-adr-cisterna.txt)
const HERO_IMAGE = { src: '/images/documentos/hero-adr.webp', position: '50% 60%' };

const COPY = {
  es: {
    seoTitle: 'Documento de transporte ADR digital, junto al DeCA | BlueCMR',
    seoDescription: 'El documento de transporte de mercancías peligrosas (ADR 5.4) en formato digital: qué debe contener y cómo emitirlo con BlueCMR en un solo documento con el DeCA.',
    seoKeywords: 'documento de transporte adr, carta de porte adr, adr 5.4.1, mercancías peligrosas documento, documento de control adr, adr digital, 1.1.3.6',
    heroEyebrow: 'Documento de transporte ADR · Mercancías peligrosas',
    heroTitle: 'El documento de transporte ADR, digital y en un solo documento',
    heroText: 'Para quien expide o transporta mercancías peligrosas: un documento digital que recoge lo que exige el capítulo 5.4 del ADR y, en el transporte nacional, hace también de DeCA.',
    heroChecks: ['ADR, capítulo 5.4', 'Nacional e internacional', 'Con el DeCA en el mismo documento'],
    platform: 'Conozca la plataforma',
    failEyebrow: 'El problema',
    failTitle: 'En inspección se falla por los detalles',
    failText: 'El ADR no impone un impreso: regula qué debe decir el documento, y ese contenido está tasado. Casi nadie desconoce que hace falta; los fallos llegan por detalles que se comprueban de forma directa y objetiva:',
    fails: [
      'Una designación que no coincide con la oficial.',
      'Menciones en un orden distinto del que exige la norma, o con datos intercalados.',
      'Un dato obligatorio que falta o un cálculo de exención mal hecho.',
    ],
    failNote: 'BlueCMR está pensado para que esos detalles no dependan de quien rellena el documento.',
    oneEyebrow: 'ADR y documento de control',
    oneTitle: 'Dos obligaciones, un solo documento',
    oneText: 'En un transporte público nacional de mercancía peligrosa conviven dos obligaciones de origen distinto. Son acumulativas: cumplir una no exime de la otra. Lo que sí permite la norma es concentrarlas en un mismo documento.',
    one: [
      { icon: ShieldAlert, title: 'Documento de transporte ADR', text: 'Obligación de seguridad: identifica qué mercancía peligrosa se transporta y en qué condiciones.', basis: 'ADR, capítulo 5.4' },
      { icon: FileCheck, title: 'DeCA', text: 'Obligación administrativa del transporte público nacional: debe ir a bordo, en formato electrónico.', basis: 'Orden FOM/2861/2012' },
    ],
    oneNote: 'Si el DeCA ya tiene que ser digital, lo sensato es que el mismo documento cumpla también el ADR, en lugar de llevar un flujo digital y otro en papel para el mismo viaje.',
    solveEyebrow: 'Con BlueCMR',
    solveTitle: 'Lo que resuelve',
    solve: [
      { title: 'Los datos oficiales de cada materia', text: 'Se identifican a partir del número ONU, sin teclear la clasificación a mano.' },
      { title: 'Las menciones, en su sitio', text: 'El documento presenta las menciones del capítulo 5.4 como exige la norma.' },
      { title: 'Los cálculos, hechos', text: 'Los totales y los cálculos de la exención por cantidades (1.1.3.6) salen calculados.' },
      { title: 'Rutas completas', text: 'Varias recogidas y varias entregas en un mismo documento.' },
      { title: 'Listo para la inspección', text: 'Código QR de verificación, historial de versiones y firma electrónica cuando se necesita.' },
      { title: 'Desde su sistema', text: 'Emisión desde la web, el móvil o su TMS o ERP mediante integración.' },
    ],
    whoEyebrow: 'Para quién',
    whoTitle: 'Pensado para quien mueve mercancía peligrosa',
    who: ['Industria química y sus operadores logísticos', 'Distribución de combustibles y gases', 'Fabricantes que expiden mercancía peligrosa envasada', 'Transportistas con flota ADR'],
    whoNote: '¿Su caso es distinto? Cuéntenoslo y le decimos con franqueza si encaja.',
    whoLink: 'Hablar con nosotros',
    faqEyebrow: 'Preguntas frecuentes',
    faqTitle: 'Dudas habituales sobre el documento ADR',
    faq: [
      { q: '¿Es obligatorio el documento de transporte ADR?', a: 'Sí. El ADR exige que toda expedición de mercancías peligrosas vaya acompañada de un documento de transporte con unas menciones determinadas (capítulo 5.4), salvo que la expedición quede amparada por alguna exención del propio ADR. No depende de que el transporte sea nacional o internacional.' },
      { q: '¿Hace falta un documento aparte además de la carta de porte?', a: 'No necesariamente. El ADR no impone un modelo ni un formato: regula el contenido. Un mismo documento puede servir de carta de porte, de DeCA y de documento de transporte ADR, siempre que recoja las menciones exigidas por cada norma. Llevar un único documento reduce el riesgo de contradicciones entre papeles.' },
      { q: '¿Puede ser electrónico?', a: 'Sí. El ADR admite el tratamiento electrónico de la información siempre que se garantice la disponibilidad de los datos durante el transporte con la misma fuerza probatoria que en papel. Además, en España el DeCA es obligatoriamente digital (Ley 9/2025).' },
      { q: '¿Qué debe indicar por cada materia transportada?', a: 'El ADR fija menciones tasadas por materia —entre otras, el número ONU, la designación oficial de transporte, los números de las etiquetas de peligro (que en la mayoría de los casos coinciden con el número de clase), el grupo de embalaje y, cuando la ruta pase por túneles con restricciones, el código de restricción en túneles— y exige que varias de ellas aparezcan en un orden determinado y sin datos intercalados. También exige el número y la descripción de los bultos y la cantidad total de cada mercancía.' },
      { q: '¿Y si el transporte se acoge a la exención de cantidades por unidad de transporte?', a: 'Acogerse a la exención del 1.1.3.6 no libera de documentación: el documento debe indicar la cantidad total y el valor calculado de las mercancías peligrosas de cada categoría de transporte. Es un cálculo que conviene no hacer a mano, porque un error puede dejar el transporte fuera de la exención sin que nadie se dé cuenta.' },
      { q: '¿Y en el transporte internacional?', a: 'El documento de transporte ADR también es obligatorio. Con BlueCMR, la información ADR puede ir dentro del propio eCMR, con la misma operativa.' },
      { q: '¿Sigue llamándose carta de porte ADR?', a: 'El ADR 2025 lo denomina documento de transporte. En su articulado, «carta de porte» queda reservada a otros documentos: el CMR, el CIM y la carta de porte aérea. La expresión «carta de porte ADR» sigue siendo de uso común en el sector y se refiere a este mismo documento.' },
    ],
    disclaimer: 'Esta página es informativa y no sustituye al asesoramiento de un consejero de seguridad ni al texto oficial del ADR.',
    relatedTitle: 'Otros documentos',
    related: [
      { to: '/documentos/documento-control', title: 'DeCA', text: 'El documento de control digital del transporte nacional.' },
      { to: '/documentos/carta-porte', title: 'Carta de porte', text: 'Contrato, documento de control y prueba de entrega en uno.' },
      { to: '/documentos/ecmr', title: 'eCMR', text: 'La carta de porte internacional, también con información ADR.' },
    ],
    closing: 'Le enseñamos el documento ADR con sus materias y sus rutas',
    closingText: 'Una demostración con su caso real, sin compromiso.',
    breadcrumb: ['Inicio', 'Documentos', 'Documento de transporte ADR'],
  },
  en: {
    seoTitle: 'Digital ADR transport document, combined with the DeCA | BlueCMR',
    seoDescription: 'The dangerous goods transport document (ADR 5.4) in digital form: what it must contain and how to issue it with BlueCMR in a single document with Spain’s DeCA.',
    seoKeywords: 'adr transport document, adr document, adr 5.4.1, dangerous goods transport document, digital adr, adr 1.1.3.6, adr spain, deca',
    heroEyebrow: 'ADR transport document · Dangerous goods',
    heroTitle: 'The ADR transport document, digital and all in one document',
    heroText: 'For consignors and carriers of dangerous goods: a digital document that contains what chapter 5.4 of ADR requires and, in domestic transport in Spain, also serves as the DeCA, Spain’s electronic control document.',
    heroChecks: ['ADR, chapter 5.4', 'Domestic and international', 'With the DeCA in the same document'],
    platform: 'Explore the platform',
    failEyebrow: 'The problem',
    failTitle: 'Inspections are failed on the details',
    failText: 'ADR does not prescribe a form: it regulates what the document must say, and that content is strictly defined. Almost everyone knows the document is required; failures come from details that are checked directly and objectively:',
    fails: [
      'A designation that does not match the proper shipping name.',
      'Particulars in a different order from the one the rules require, or with other data inserted between them.',
      'A missing mandatory item or a miscalculated exemption.',
    ],
    failNote: 'BlueCMR is designed so that those details do not depend on whoever fills in the document.',
    oneEyebrow: 'ADR and control document',
    oneTitle: 'Two obligations, one document',
    oneText: 'In domestic hire-and-reward transport of dangerous goods in Spain, two obligations of different origin apply. They are cumulative: meeting one does not exempt you from the other. What the rules do allow is combining them in the same document.',
    one: [
      { icon: ShieldAlert, title: 'ADR transport document', text: 'Safety obligation: it identifies which dangerous goods are carried and under what conditions.', basis: 'ADR, chapter 5.4' },
      { icon: FileCheck, title: 'DeCA', text: 'Administrative obligation for domestic hire-and-reward transport in Spain: it must be on board, in electronic form.', basis: 'Order FOM/2861/2012' },
    ],
    oneNote: 'If the DeCA already has to be digital, the sensible approach is for the same document to meet ADR as well, rather than running one digital flow and another on paper for the same journey.',
    solveEyebrow: 'With BlueCMR',
    solveTitle: 'What it solves',
    solve: [
      { title: 'The official data for each substance', text: 'Identified from the UN number, without typing the classification by hand.' },
      { title: 'Particulars in their place', text: 'The document presents the chapter 5.4 particulars as the rules require.' },
      { title: 'Calculations done for you', text: 'Totals and the calculations for the quantity exemption (1.1.3.6) are worked out automatically.' },
      { title: 'Complete routes', text: 'Several collections and several deliveries in the same document.' },
      { title: 'Ready for inspection', text: 'Verification QR code, version history and electronic signature when needed.' },
      { title: 'From your system', text: 'Issue from the web, mobile or your TMS or ERP through integration.' },
    ],
    whoEyebrow: 'Who it is for',
    whoTitle: 'Designed for those who move dangerous goods',
    who: ['The chemical industry and its logistics operators', 'Fuel and gas distribution', 'Manufacturers consigning packaged dangerous goods', 'Carriers with an ADR fleet'],
    whoNote: 'Is your case different? Tell us about it and we will tell you frankly whether it fits.',
    whoLink: 'Talk to us',
    faqEyebrow: 'FAQ',
    faqTitle: 'Common questions about the ADR document',
    faq: [
      { q: 'Is the ADR transport document mandatory?', a: 'Yes. ADR requires every consignment of dangerous goods to be accompanied by a transport document with specific particulars (chapter 5.4), unless the consignment is covered by one of the exemptions in ADR itself. It does not depend on whether the transport is domestic or international.' },
      { q: 'Is a separate document needed in addition to the consignment note?', a: 'Not necessarily. ADR does not prescribe a model or a format: it regulates the content. A single document can serve as consignment note, DeCA and ADR transport document, as long as it includes the particulars required by each set of rules. Carrying a single document reduces the risk of contradictions between papers.' },
      { q: 'Can it be electronic?', a: 'Yes. ADR allows the information to be processed electronically, provided that the availability of the data during transport is guaranteed with the same evidential value as on paper. In addition, in Spain the DeCA must be digital (Law 9/2025).' },
      { q: 'What must it state for each substance carried?', a: 'ADR sets strictly defined particulars for each substance —among others, the UN number, the proper shipping name, the hazard label numbers (which in most cases match the class number), the packing group and, when the route passes through tunnels with restrictions, the tunnel restriction code— and requires several of them to appear in a set order with no other data inserted between them. It also requires the number and description of the packages and the total quantity of each item of dangerous goods.' },
      { q: 'What if the transport uses the exemption for quantities carried per transport unit?', a: 'Using the 1.1.3.6 exemption does not release you from documentation: the document must state the total quantity and the calculated value of the dangerous goods in each transport category. It is a calculation best not done by hand, because an error can take the transport outside the exemption without anyone noticing.' },
      { q: 'What about international transport?', a: 'The ADR transport document is also mandatory. With BlueCMR, the ADR information can go in the eCMR itself, with the same workflow.' },
      { q: 'Is it still called “carta de porte ADR”?', a: 'In Spanish, ADR 2025 calls it “documento de transporte” (transport document). In its text, “carta de porte” (consignment note) is reserved for other documents: the CMR, the CIM and the air waybill. The expression “carta de porte ADR” is still common in the Spanish sector and refers to this same document.' },
    ],
    disclaimer: 'This page is for information only and does not replace the advice of a dangerous goods safety adviser or the official text of ADR.',
    relatedTitle: 'Other documents',
    related: [
      { to: '/documentos/documento-control', title: 'DeCA', text: 'Spain’s digital control document for domestic transport.' },
      { to: '/documentos/carta-porte', title: 'Consignment note (Spain)', text: 'Contract, control document and proof of delivery in one.' },
      { to: '/documentos/ecmr', title: 'eCMR', text: 'The international consignment note, also with ADR information.' },
    ],
    closing: 'Let us show you the ADR document with your substances and routes',
    closingText: 'A demonstration with your real case, with no commitment.',
    breadcrumb: ['Home', 'Documents', 'ADR transport document'],
  },
};

const TransporteADRDocumentoPage = () => {
  const { i18n } = useTranslation();
  const lang = i18n.language === 'es' ? 'es' : 'en';
  const c = COPY[lang];
  const { getLocalizedUrl } = useLocalizedUrl();
  const primaryCta = usePrimaryCta(lang);

  return (
    <>
      <SeoMeta
        title={c.seoTitle}
        description={c.seoDescription}
        canonical={PAGE_URL[lang]}
        type="website"
        alternates={{ es: PAGE_URL.es, en: PAGE_URL.en, 'x-default': PAGE_URL.es }}
        keywords={c.seoKeywords}
        breadcrumbs={[
          { name: c.breadcrumb[0], url: lang === 'es' ? 'https://www.bluecmr.com/' : 'https://www.bluecmr.com/en' },
          { name: c.breadcrumb[1], url: 'https://www.bluecmr.com/documentos' },
          { name: c.breadcrumb[2], url: PAGE_URL[lang] }
        ]}
        structuredData={[
          { '@context': 'https://schema.org', '@type': 'WebPage', name: c.heroTitle, description: c.seoDescription, url: PAGE_URL[lang] },
          faqSchema(c.faq)
        ]}
      />

      <div className="bg-white">
        <PageHero
          eyebrow={c.heroEyebrow}
          title={c.heroTitle}
          text={c.heroText}
          image={HERO_IMAGE}
          primary={primaryCta}
          secondary={{ to: getLocalizedUrl('/plataforma'), label: c.platform }}
          compact
        >
          <ul className="mt-8 flex flex-wrap gap-x-6 gap-y-2">
            {c.heroChecks.map((item) => (
              <li key={item} className="flex items-center gap-2 text-slate-200">
                <Check className="w-4 h-4 text-blue-300" strokeWidth={2.5} />
                {item}
              </li>
            ))}
          </ul>
        </PageHero>

        {/* El problema: los detalles */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.failEyebrow} title={c.failTitle} className="" />
            </div>
            <div className="lg:col-span-7">
              <p className="text-lg text-slate-600 leading-relaxed">{c.failText}</p>
              <ol className="mt-6 border-b border-slate-200">
                {c.fails.map((item, i) => (
                  <li key={item} className="flex items-baseline gap-4 border-t border-slate-200 py-4 text-slate-800">
                    <span className="text-sm font-semibold text-primary-700 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                    <span>{item}</span>
                  </li>
                ))}
              </ol>
              <p className="mt-8 border-l-2 border-slate-900 pl-5 text-slate-700 leading-relaxed">{c.failNote}</p>
            </div>
          </div>
        </section>

        {/* Un documento, no tres */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.oneEyebrow} title={c.oneTitle} text={c.oneText} />
            <div className="grid md:grid-cols-2 gap-x-12 gap-y-10">
              {c.one.map((item) => {
                const Icon = item.icon;
                return (
                  <div key={item.title} className="border-t-2 border-slate-900 pt-6">
                    <Icon className="w-7 h-7 text-primary-700 mb-5" strokeWidth={1.5} />
                    <h3 className="text-xl font-semibold text-slate-900">{item.title}</h3>
                    <p className="mt-3 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
                    <p className="mt-4 text-sm text-slate-500">{item.basis}</p>
                  </div>
                );
              })}
            </div>
            <p className="mt-12 max-w-3xl border-l-2 border-slate-900 pl-5 text-lg text-slate-700 leading-relaxed">{c.oneNote}</p>
          </div>
        </section>

        {/* Lo que resuelve */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionIntro eyebrow={c.solveEyebrow} eyebrowAsIs title={c.solveTitle} />
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-10 gap-y-10">
              {c.solve.map((item, i) => (
                <div key={item.title} className="border-t border-slate-300 pt-5">
                  <span className="text-sm font-semibold text-primary-700 tabular-nums">{String(i + 1).padStart(2, '0')}</span>
                  <h3 className="mt-2 font-semibold text-slate-900">{item.title}</h3>
                  <p className="mt-2 text-[15px] text-slate-600 leading-relaxed">{item.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Para quién */}
        <section className="py-20 md:py-24 bg-slate-50 border-y border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-5">
              <SectionIntro eyebrow={c.whoEyebrow} title={c.whoTitle} className="" />
              <p className="mt-6 text-slate-600 leading-relaxed">
                {c.whoNote}{' '}
                <Link to={getLocalizedUrl('/contacto')} className="font-semibold text-primary-700 hover:underline">{c.whoLink}</Link>.
              </p>
            </div>
            <ul className="lg:col-span-7 border-b border-slate-300">
              {c.who.map((item) => (
                <li key={item} className="flex items-center gap-3 border-t border-slate-300 py-4 text-slate-800">
                  <Check className="w-5 h-5 flex-shrink-0 text-primary-700" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        </section>

        {/* Preguntas frecuentes */}
        <section className="py-20 md:py-24">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10 lg:gap-16">
            <div className="lg:col-span-4">
              <SectionIntro eyebrow={c.faqEyebrow} title={c.faqTitle} className="" />
            </div>
            <div className="lg:col-span-8">
              <FaqList items={c.faq} />
              <p className="mt-6 text-sm text-slate-500">{c.disclaimer}</p>
            </div>
          </div>
        </section>

        {/* Otros documentos */}
        <section className="py-14 border-t border-slate-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 mb-6">{c.relatedTitle}</h2>
            <ul className="grid md:grid-cols-3 gap-x-8">
              {c.related.map((r) => (
                <li key={r.to} className="border-t border-slate-200">
                  <Link to={getLocalizedUrl(r.to)} className="group flex items-start justify-between gap-4 py-5">
                    <span>
                      <span className="block font-semibold text-slate-900 group-hover:text-primary-700 transition-colors">{r.title}</span>
                      <span className="mt-1 block text-sm text-slate-600">{r.text}</span>
                    </span>
                    <ArrowRight className="mt-1 w-5 h-5 flex-shrink-0 text-slate-400 group-hover:text-primary-700 group-hover:translate-x-1 transition" />
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <ClosingCta title={c.closing} text={c.closingText} lang={lang} />
      </div>
    </>
  );
};

export default TransporteADRDocumentoPage;
